import React, { useEffect, useRef, useCallback } from 'react';
import InvoiceCard from '../../organization/InvoiceCard';
import { UnpaidInvoice } from '../../../types/organizations';

interface InvoiceCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: UnpaidInvoice | null;
  organizationId: string;
}

const InvoiceCardModal: React.FC<InvoiceCardModalProps> = ({
  isOpen,
  onClose,
  invoice,
  organizationId
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  // Handle Escape key
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Focus trap functionality
  const handleTabKey = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  }, []);

  // Set up event listeners and focus management
  useEffect(() => {
    if (!isOpen) return;

    // Store the previously focused element
    previouslyFocusedElement.current = document.activeElement as HTMLElement;

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleTabKey);

    // Focus the close button initially
    const timer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleTabKey);
      clearTimeout(timer);
      
      // Restore focus to previously focused element
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, [isOpen, handleKeyDown, handleTabKey]);

  if (!isOpen || !invoice) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black opacity-30" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div 
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-modal-title"
        aria-describedby="invoice-modal-content"
        tabIndex={-1}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 
              id="invoice-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              Invoice Details
            </h3>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1"
              aria-label="Close invoice details modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div 
            id="invoice-modal-content"
          >
            <InvoiceCard
              invoice={invoice}
              onViewAnalyses={() => {}} // No analysis viewing in this context
              organizationId={organizationId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCardModal;
